import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import {
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowRight,
  CaretDown,
  Check,
  Clock,
  Lightning,
} from "@phosphor-icons/react";
import { guides } from "../../data/content.js";
import { EASE_OUT, MaskWords, PageFrame, Photo, Reveal, Seo, useMediaQuery } from "../../kit.jsx";
import { GuideGlyph } from "./Glyphs.jsx";
import { GUIDE_ART } from "./learnData.js";
import LearnNotFound from "./LearnNotFound.jsx";
import "./learn.css";

const STORE_PREFIX = "grid-guide-checklist:";

function readTicks(slug) {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + slug);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => Number.isInteger(value)) : [];
  } catch {
    return [];
  }
}

function writeTicks(slug, ticks) {
  try {
    if (ticks.length) window.localStorage.setItem(STORE_PREFIX + slug, JSON.stringify(ticks));
    else window.localStorage.removeItem(STORE_PREFIX + slug);
  } catch {
    // Storage can be blocked. Ticks then last for this visit only.
  }
}

function useActiveSection(ids) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const line = window.innerHeight * 0.35;
      let current = 0;
      ids.forEach((id, index) => {
        const node = document.getElementById(id);
        if (node && node.getBoundingClientRect().top <= line) current = index;
      });
      setActive((previous) => (previous === current ? previous : current));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ids]);
  return active;
}

function HeroPhoto({ src, alt, caption }) {
  const reduceMotion = useReducedMotion();
  return (
    <figure className="lb-gh__photo">
      <motion.div
        className="lb-gh__frame"
        initial={reduceMotion ? false : { clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.1, delay: 0.25, ease: EASE_OUT }}
      >
        <motion.div
          className="lb-gh__zoom"
          initial={reduceMotion ? false : { scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, delay: 0.25, ease: EASE_OUT }}
        >
          <Photo src={src} alt={alt} priority sizes="(max-width: 900px) 100vw, 45vw" />
        </motion.div>
      </motion.div>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function GuideHero({ guide, index }) {
  const reduceMotion = useReducedMotion();
  const art = GUIDE_ART[guide.slug];
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <header className={`lb-gh lb-gh--${art.variant}`}>
      <motion.nav className="lb-gh__crumb" aria-label="Breadcrumb" {...appear(0)}>
        <Link to="/guides">Field manual</Link>
        <span aria-hidden="true">/</span>
        <span>Stop {index + 1} of {guides.length} · {art.stop}</span>
      </motion.nav>
      <div className="lb-gh__title">
        <MaskWords as="h1" text={guide.title} onMount delay={0.05} />
        <motion.p {...appear(0.35)}>{guide.summary}</motion.p>
      </div>
      <GuideGlyph className="lb-gh__glyph" variant={art.variant} delay={0.45} />
      <HeroPhoto src={art.photo} alt={art.alt} caption={art.caption} />
      <motion.dl className="lb-gh__facts" {...appear(0.5)}>
        <div><dt>Reading time</dt><dd>{guide.readTime}</dd></div>
        <div><dt>Steps</dt><dd>{guide.sections.length}, each with a task</dd></div>
        <div><dt>Ends with</dt><dd>A {guide.checklist.length}-point checklist</dd></div>
      </motion.dl>
    </header>
  );
}

function Toc({ items, active, progress, readTime }) {
  const wide = useMediaQuery("(min-width: 1000px)");
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const scaleY = useTransform(progress, (value) => Math.min(1, Math.max(0, value)));
  const current = items[active];

  if (!wide) {
    return (
      <div className="lb-tocbar">
        <button type="button" className="lb-tocbar__toggle" aria-expanded={open} aria-controls="lb-tocbar-list" onClick={() => setOpen(!open)}>
          <span className="lb-tocbar__count">{active + 1}/{items.length}</span>
          <strong>{current.label}</strong>
          <CaretDown weight="bold" aria-hidden="true" />
        </button>
        <span className="lb-tocbar__track" aria-hidden="true">
          <motion.span style={reduceMotion ? { scaleX: 1 } : { scaleX: scaleY }} />
        </span>
        <AnimatePresence initial={false}>
          {open && (
            <motion.ol
              id="lb-tocbar-list"
              className="lb-tocbar__list"
              initial={reduceMotion ? false : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: 0.24, ease: EASE_OUT }}
            >
              {items.map((item, index) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} aria-current={index === active ? "location" : undefined} onClick={() => setOpen(false)}>
                    <span>{item.num}</span>{item.label}
                  </a>
                </li>
              ))}
            </motion.ol>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <nav className="lb-toc" aria-label="In this guide">
      <p className="lb-mono">In this guide</p>
      <div className="lb-toc__body">
        <span className="lb-toc__track" aria-hidden="true">
          <motion.span style={reduceMotion ? { scaleY: 1 } : { scaleY }} />
        </span>
        <ol>
          {items.map((item, index) => (
            <li key={item.id} className={index === active ? "is-active" : index < active ? "is-passed" : undefined}>
              <a href={`#${item.id}`} aria-current={index === active ? "location" : undefined}>
                <span>{item.num}</span>{item.label}
              </a>
            </li>
          ))}
        </ol>
      </div>
      <p className="lb-toc__time"><Clock weight="bold" aria-hidden="true" />{readTime} read</p>
    </nav>
  );
}

function Checklist({ guide, nextGuide }) {
  const reduceMotion = useReducedMotion();
  const [ticks, setTicks] = useState(() => readTicks(guide.slug));
  const total = guide.checklist.length;
  const doneCount = ticks.length;

  useEffect(() => {
    setTicks(readTicks(guide.slug));
  }, [guide.slug]);

  function toggle(index) {
    setTicks((previous) => {
      const next = previous.includes(index) ? previous.filter((value) => value !== index) : [...previous, index].sort();
      writeTicks(guide.slug, next);
      return next;
    });
  }

  function reset() {
    writeTicks(guide.slug, []);
    setTicks([]);
  }

  return (
    <section id="checklist" className="lb-check" aria-labelledby="lb-check-title">
      <div className="lb-check__head">
        <span className="lb-mono">Before you move on</span>
        <h2 id="lb-check-title">Run the final check.</h2>
        <p>Tick each line you can honestly say yes to. Your ticks stay on this device.</p>
      </div>
      <div className="lb-check__meter" aria-hidden="true">
        <motion.span
          initial={false}
          animate={{ scaleX: doneCount / total }}
          transition={{ duration: reduceMotion ? 0 : 0.5, ease: EASE_OUT }}
        />
      </div>
      <p className="lb-check__count" aria-live="polite">{doneCount} of {total} done</p>
      <ul>
        {guide.checklist.map((item, index) => {
          const checked = ticks.includes(index);
          return (
            <li key={item} className={checked ? "is-checked" : undefined}>
              <label>
                <input type="checkbox" checked={checked} onChange={() => toggle(index)} />
                <span className="lb-check__box" aria-hidden="true"><Check weight="bold" /></span>
                <span className="lb-check__text">{item}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="lb-check__foot">
        {doneCount === total ? (
          <p className="lb-check__ready">
            All {total} checked.{" "}
            {nextGuide ? (
              <Link className="gh-link" to={`/guides/${nextGuide.slug}`}>Go to {nextGuide.title.toLowerCase()} <ArrowRight weight="bold" aria-hidden="true" /></Link>
            ) : (
              <span>That is the end of the field manual.</span>
            )}
          </p>
        ) : (
          <p className="lb-check__hint">A line you can't tick yet is your next task.</p>
        )}
        {doneCount > 0 && (
          <button type="button" className="lb-check__reset" onClick={reset}>
            <ArrowCounterClockwise weight="bold" aria-hidden="true" /> Clear ticks
          </button>
        )}
      </div>
    </section>
  );
}

function PrevNext({ prevGuide, nextGuide, index }) {
  return (
    <nav className="lb-pn" aria-label="Previous and next guide">
      {prevGuide ? (
        <Link className="lb-pn__link lb-pn__link--prev" to={`/guides/${prevGuide.slug}`}>
          <span className="lb-mono">Previous · stop {index}</span>
          <strong>{prevGuide.title}</strong>
          <small>{prevGuide.summary}</small>
          <ArrowLeft weight="bold" aria-hidden="true" />
        </Link>
      ) : (
        <Link className="lb-pn__link lb-pn__link--prev" to="/guides">
          <span className="lb-mono">Back to the start</span>
          <strong>The field manual</strong>
          <small>All five guides, in order, with reading times.</small>
          <ArrowLeft weight="bold" aria-hidden="true" />
        </Link>
      )}
      {nextGuide ? (
        <Link className="lb-pn__link lb-pn__link--next" to={`/guides/${nextGuide.slug}`}>
          <span className="lb-mono">Next · stop {index + 2}</span>
          <strong>{nextGuide.title}</strong>
          <small>{nextGuide.summary}</small>
          <ArrowRight weight="bold" aria-hidden="true" />
        </Link>
      ) : (
        <Link className="lb-pn__link lb-pn__link--next" to="/projects">
          <span className="lb-mono">You finished the route</span>
          <strong>See what students have built</strong>
          <small>Real projects from GRID programmes, and how to share yours.</small>
          <ArrowRight weight="bold" aria-hidden="true" />
        </Link>
      )}
    </nav>
  );
}

function GuideArticle({ guide }) {
  const index = guides.findIndex((item) => item.slug === guide.slug);
  const prevGuide = guides[index - 1];
  const nextGuide = guides[index + 1];
  const articleRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: articleRef, offset: ["start 0.35", "end 0.65"] });

  const items = useMemo(
    () => [
      ...guide.sections.map((section, i) => ({ id: `step-${i + 1}`, num: String(i + 1).padStart(2, "0"), label: section.title })),
      { id: "example", num: "Ex", label: "Before and after" },
      { id: "checklist", num: "✓", label: "Final check" },
    ],
    [guide],
  );
  const ids = useMemo(() => items.map((item) => item.id), [items]);
  const active = useActiveSection(ids);

  const schema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: guide.title,
      description: guide.summary,
      totalTime: `PT${guide.readTime.replace(/\D/g, "")}M`,
      step: guide.sections.map((section, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: section.title,
        text: `${section.body} ${section.action}`,
      })),
    }),
    [guide],
  );

  return (
    <PageFrame className={`rd-page learn-page lb-guide lb-guide--${GUIDE_ART[guide.slug].variant}`}>
      <Seo title={guide.title} path={`/guides/${guide.slug}`} description={guide.summary} type="article" schema={schema} />
      <main>
        <GuideHero guide={guide} index={index} />

        <section className="lb-outcome" aria-labelledby="lb-outcome-title">
          <span className="lb-mono">What you will leave with</span>
          <MaskWords as="p" id="lb-outcome-title" className="lb-outcome__text" text={guide.outcome} />
        </section>

        <div className="lb-read">
          <Toc items={items} active={active} progress={scrollYProgress} readTime={guide.readTime} />
          <article className="lb-article" ref={articleRef}>
            {guide.sections.map((section, i) => (
              <section key={section.title} id={`step-${i + 1}`} className="lb-step" aria-labelledby={`step-${i + 1}-title`}>
                <span className="lb-step__num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <h2 id={`step-${i + 1}-title`}>{section.title}</h2>
                <p>{section.body}</p>
                <Reveal className="lb-callout" preset="soft">
                  <span className="lb-callout__icon" aria-hidden="true"><Lightning weight="fill" /></span>
                  <div>
                    <span className="lb-mono">Do this now</span>
                    <p>{section.action}</p>
                  </div>
                </Reveal>
              </section>
            ))}

            <section id="example" className="lb-example" aria-labelledby="lb-example-title">
              <span className="lb-mono">{guide.example.label}</span>
              <h2 id="lb-example-title">Make the change visible.</h2>
              <div className="lb-example__pair">
                <Reveal className="lb-example__before" preset="soft">
                  <span className="lb-example__tag">Before</span>
                  <p>{guide.example.before}</p>
                </Reveal>
                <Reveal className="lb-example__after" preset="soft" delay={0.12}>
                  <span className="lb-example__tag lb-example__tag--after">After</span>
                  <p>{guide.example.after}</p>
                </Reveal>
              </div>
            </section>

            <Checklist guide={guide} nextGuide={nextGuide} />
          </article>
        </div>

        <PrevNext prevGuide={prevGuide} nextGuide={nextGuide} index={index} />
      </main>
    </PageFrame>
  );
}

export default function GuidePage() {
  const { slug } = useParams();
  const guide = guides.find((item) => item.slug === slug);
  if (!guide) return <LearnNotFound kind="guide" />;
  return <GuideArticle key={guide.slug} guide={guide} />;
}
